// MyItemsScreen — what a logged-in Contact (subbie / trade) sees.
//
// v0.43.0 — initial. Read-only list of defects where AssignedContactId
//           === current contact, scoped across all projects the contact
//           has been added to. Grouped by Project. Status filter chip
//           row up top. Sign-out lives in the header.
//
// Rectify / photo-evidence-upload from the subbie view = v0.43.1.
//
// Auth: the signed httpOnly droppin_contact cookie set by /invite/<token>
// (v0.49.0) is the session; the 'droppin.contact.v1' localStorage payload
// is a UI cache only. AppShell branches on that key to render this screen
// without the admin chrome. A 401 from the API = expired/invalid session —
// we clear the cache and show a "get a new invite link" card.

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { apiGet } from '../api';
import { theme, card } from '../theme';
// v0.46.7 — Shared page title (standardised heading size).
import PageHeading from '../components/PageHeading';
import { getContactSession, clearContactSession } from '../utils/contact-session';
import { tradeColor, tradeLabel } from '../utils/trades';
import { STATUS_LABEL, normaliseStatus, statusLabel, isOutstanding } from '../utils/defectStatus';
// v0.43.1 — Mark-rectified flow.
import SubbieRectifyModal from '../components/SubbieRectifyModal';
import DropPinWordmark from '../components/DropPinWordmark';
// v0.50.0 — lucide icons replace emoji glyphs in defect meta row.
import { MapPin, Ruler, Camera } from 'lucide-react';

export default function MyItemsScreen() {
  const navigate = useNavigate();
  const session = getContactSession();
  const [defects, setDefects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState('open'); // 'all' | 'open' | 'on_hold' | 'rectified' | 'complete'
  // v0.43.1 — The defect currently being rectified (modal target).
  const [rectifying, setRectifying] = useState(null);
  // v0.49.0 — true once the API says our signed session is gone/expired.
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    if (!session?.contact?.id) {
      navigate('/', { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        // v0.49.0 — identity rides in the httpOnly droppin_contact
        // cookie; the query string only carries the status filter.
        const qs = filter !== 'all' ? `?status=${filter}` : '';
        const list = await apiGet(`/api/contact-defects${qs}`);
        if (!cancelled) setDefects(Array.isArray(list) ? list : []);
      } catch (e) {
        if (cancelled) return;
        const msg = e?.message || String(e);
        // v0.49.0 — a 401 means the signed session is missing/expired.
        // Clear the stale UI cache and show the re-login card.
        if (/→ 401|session (expired|invalid|no longer valid)|not signed in/i.test(msg)) {
          clearContactSession();
          setSessionExpired(true);
        } else {
          setErr(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [filter]);

  // Group defects by project for readability.
  const grouped = useMemo(() => {
    const m = new Map();
    for (const d of defects) {
      const key = d.projectId;
      if (!m.has(key)) m.set(key, { name: d.projectName, address: d.projectAddress, items: [] });
      m.get(key).items.push(d);
    }
    return Array.from(m.entries());
  }, [defects]);

  function signOut() {
    // v0.49.0 — the session cookie is httpOnly; ask the server to
    // clear it (fire-and-forget), then drop the local UI cache.
    fetch('/api/contact-logout', { method: 'POST' }).catch(() => {});
    clearContactSession();
    navigate('/', { replace: true });
  }

  const contact = session?.contact;

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, color: theme.text, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Subbie chrome — minimal header, no nav. */}
      <header style={{
        background: 'linear-gradient(160deg,#0f172a 0%,#1e293b 100%)',
        color: 'white',
        padding: '14px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 26, height: 26, borderRadius: 6,
            background: theme.brand,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 800, color: '#0f172a',
          }}>{'\u{1F4CD}'}</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}><DropPinWordmark /></div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>Subcontractor view</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {contact?.name && (
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)' }}>
              {contact.name}
              {contact.company && <span style={{ color: 'rgba(255,255,255,0.55)' }}> · {contact.company}</span>}
            </div>
          )}
          <button
            type="button"
            onClick={signOut}
            style={{
              background: 'rgba(255,255,255,0.1)', color: 'white', border: 0,
              padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
            }}
          >Sign out</button>
        </div>
      </header>

      <main style={{ maxWidth: 980, margin: '0 auto', padding: '20px 16px' }}>
        <PageHeading>My items</PageHeading>
        <div style={{ fontSize: 13, color: theme.textLight, marginTop: 2, marginBottom: 14 }}>
          Defects assigned to you across all your projects.
        </div>

        {/* Filter chips */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          {['open', 'on_hold', 'rectified', 'complete', 'all'].map(k => (
            <button
              key={k}
              type="button"
              onClick={() => setFilter(k)}
              style={{
                padding: '6px 12px',
                borderRadius: 999,
                border: `1px solid ${filter === k ? theme.brand : theme.border}`,
                background: filter === k ? theme.bg : theme.surface,
                color: filter === k ? theme.brand : theme.text,
                fontSize: 12, fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >{k === 'all' ? 'All' : STATUS_LABEL[k]}</button>
          ))}
        </div>

        {err && (
          <div style={{ padding: 12, background: theme.bg, border: '1px solid ' + theme.danger, color: theme.danger, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
            {err}
          </div>
        )}

        {sessionExpired ? (
          <div style={{ ...card, padding: 30, textAlign: 'center' }}>
            <div style={{ fontSize: 24, marginBottom: 6 }}>{'\u{23F3}'}</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Your sign-in has expired.</div>
            <div style={{ fontSize: 12, color: theme.textLight, marginTop: 4 }}>
              Ask the builder to send you a new invite link — opening it signs you straight back in.
            </div>
          </div>
        ) : loading ? (
          <div style={{ ...card, padding: 30, textAlign: 'center', color: theme.textLight }}>Loading…</div>
        ) : defects.length === 0 ? (
          <div style={{ ...card, padding: 30, textAlign: 'center' }}>
            <div style={{ fontSize: 24, marginBottom: 6 }}>{'\u{2705}'}</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>
              {filter === 'open' ? 'No open items.' : 'Nothing here yet.'}
            </div>
            <div style={{ fontSize: 12, color: theme.textLight, marginTop: 4 }}>
              {filter === 'open'
                ? 'Either you\'ve cleared the list or nothing has been assigned to you.'
                : 'Items will appear here once they\'ve been assigned to you.'}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {grouped.map(([projectId, group]) => (
              <div key={projectId}>
                <div style={{
                  fontSize: 11, fontWeight: 800, color: theme.textLight,
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                  marginBottom: 6,
                }}>
                  {group.name}
                  {group.address && <span style={{ color: theme.textLight, fontWeight: 500 }}> · {group.address}</span>}
                  <span style={{ color: theme.textLight, fontWeight: 500 }}> · {group.items.length}</span>
                </div>
                <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
                  {group.items.map((d, i) => (
                    <DefectRow
                      key={d.id}
                      d={d}
                      divider={i < group.items.length - 1}
                      onOpen={() => {
                        // Only outstanding items (Open / On hold) are actionable
                        // from the subbie view; a cancelled one never is (v0.312.0).
                        if (!isOutstanding(d.status)) return;
                        setRectifying(d);
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* v0.43.1 — Mark rectified. Refreshes the list on success. */}
      {rectifying && (
        <SubbieRectifyModal
          defect={rectifying}
          onCancel={() => setRectifying(null)}
          onRectified={() => {
            setRectifying(null);
            // Re-trigger the load by flipping the filter back to itself.
            setFilter(f => f);
            // Quick local removal so it disappears immediately even
            // if the network is slow.
            setDefects(prev => prev.filter(x => x.id !== rectifying.id));
          }}
        />
      )}
    </div>
  );
}

// v0.312.0 — one source for the five statuses (On hold, Cancelled added).
// Theme tokens, not the PDF's hex, so the pill follows dark mode.
const PILL_FG = {
  open:      theme.danger,
  on_hold:   theme.hold,
  rectified: theme.warn,
  complete:  theme.success,
  cancelled: theme.textLight,
};
function statusPill(status) {
  return { label: statusLabel(status), bg: theme.bg, fg: PILL_FG[normaliseStatus(status)], actionable: isOutstanding(status) };
}

function DefectRow({ d, divider, onOpen }) {
  const pill = statusPill(d.status);
  const isOpen = pill.actionable;
  return (
    <div
      onClick={isOpen ? onOpen : undefined}
      role={isOpen ? 'button' : undefined}
      style={{
      display: 'grid',
      gridTemplateColumns: 'auto 1fr auto',
      gap: 10,
      padding: '10px 14px',
      borderBottom: divider ? `1px solid ${theme.border}` : 'none',
      alignItems: 'center',
      cursor: isOpen ? 'pointer' : 'default',
    }}>
      <span style={{
        width: 10, height: 10, borderRadius: '50%',
        background: d.trade ? tradeColor(d.trade) : theme.textLight,
        alignSelf: 'center',
      }} title={d.trade ? tradeLabel(d.trade) : 'No trade'} />
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>
          {d.defectNumber != null && <span style={{ color: theme.textLight, fontWeight: 500, marginRight: 6 }}>#{d.defectNumber}</span>}
          {d.description || '(no description)'}
        </div>
        <div style={{ fontSize: 11, color: theme.textLight, marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {d.locationLabel && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}><MapPin size={10} />{d.locationLabel}</span>}
          {d.drawingName && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}><Ruler size={10} />{d.drawingName}</span>}
          {d.photoCount > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}><Camera size={10} />{d.photoCount}</span>}
          {d.priority && <span style={{ textTransform: 'capitalize' }}>· {d.priority}</span>}
        </div>
      </div>
      <span style={{
        background: pill.bg, color: pill.fg,
        padding: '3px 8px', borderRadius: 999,
        fontSize: 11, fontWeight: 700,
      }}>{pill.label}</span>
    </div>
  );
}
